'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { IconSearch, IconClose, IconArrowRight } from '@/app/components/atoms/Icons'
import { SearchBarGlobal } from '@/app/components/atoms/SearchBarGlobal'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { Wordmark } from '@/app/components/atoms/Wordmark'
import { useOptionalChromeAnalytics } from '@/app/context/analytics'
import { NO_APLICA, NOT_SET } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import { cx, lockDocumentScroll } from '@/app/lib/utils'
import { routes } from '@/app/lib/routing'
import { searchAutocompleteAction } from './actions'
import {
  mapSearchProductToAnalyticsItem,
  toSearchProductSelectItem,
} from './mapSearchProductToAnalyticsItem'
import { mapSearchProductToCard } from './mapSearchProductToCard'
import type { SearchProduct, Suggestion, TopSearch } from './types'
import styles from './SearchInput.module.scss'

/**
 * Analítica (`components-fingerprint/Header.csv`, módulo transversal — este
 * componente se monta tanto en el header como en `SearchBand` de la Home).
 * `module_name`/`search_origin` son valores LITERALES del CSV/diccionario
 * (`search_origin`: "Siempre 'header' para el buscador principal" — se
 * mantiene aunque el submit ocurra desde `SearchBand`, ver `types.ts`
 * `analytic-variables.md`).
 */
const SEARCH_MODULE_NAME = 'header'
const SEARCH_ORIGIN = 'header'

/**
 * `view_item_list`/`select_item` de "Productos más buscados"
 * (`components-fingerprint/Resultados de búsqueda.csv`, sección homónima):
 * el CSV documenta `items[]` pero NO los parámetros de envoltorio
 * (`item_list_id`/`item_list_name`/`module_type`/`module_position`) que
 * exige `ViewItemListParams`/`SelectItemParams` — a diferencia de la versión
 * de este mismo evento en el grid del SERP, más arriba en el mismo CSV, que
 * sí los lista (`item_list_id: serp_results_grid`, `module_name: buscador`…).
 * ⚠️ VALORES INFERIDOS, pendientes de confirmar con Analítica: `module_name`
 * reutiliza `SEARCH_MODULE_NAME` ("header", ya usado por el resto de eventos
 * de este componente); el resto son literales nuevos siguiendo la misma
 * convención snake_case del resto del catálogo.
 */
const SEARCH_PRODUCTS_ITEM_LIST_ID = 'header_products_most_searched'
const SEARCH_PRODUCTS_ITEM_LIST_NAME = 'productos_mas_buscados'
const SEARCH_PRODUCTS_MODULE_TYPE = 'search_products_most_searched'
const SEARCH_PRODUCTS_MODULE_POSITION = '1'

/**
 * `click`/`content_link` de un chip de "Top Búsquedas"
 * (`components-fingerprint/Resultados de búsqueda.csv` → «Click en sugerencia
 * de Top búsquedas»). Valores LITERALES de esa ficha — distintos de
 * `SEARCH_MODULE_NAME`/`SEARCH_ORIGIN` (`Header.csv`), que pertenecen al
 * submit libre del buscador, no a este chip.
 */
const TOP_SEARCHES_MODULE_NAME = 'top_busquedas'
const TOP_SEARCHES_MODULE_TYPE = 'search_top_queries'

export interface SearchInputProps {
  /** Sugerencias (de `module_search.top_search` del layout). */
  topSearches?: TopSearch[]
  /** Productos más buscados (de `module_search.products_most_searched`). */
  products?: SearchProduct[]
  placeholder?: string
  onOpenChange?: (open: boolean) => void
  /** `full` → píldora `SearchBarGlobal`; `icon` → botón de lupa (lanzador
   * compacto de barra de navegación); `compact` (por defecto) → caja del header. */
  variant?: 'compact' | 'full' | 'icon'
  /** Solo con `variant="full"`: aplica el estilo verde de la banda de búsqueda. */
  bandStyle?: boolean
  /** Solo con `variant="full"`: tema inverse para el header oscuro DS (borde
   * gris, texto blanco, botón verde acento). */
  inverse?: boolean
}

function highlightSearchTerm(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm) return text

  const regex = new RegExp(`(${searchTerm})`, 'gi')
  const parts = text.split(regex)

  return (
    <span>
      {parts.map((part, index) => {
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
          return <span key={index}>{part}</span>
        }
        return <strong key={index}>{part}</strong>
      })}
    </span>
  )
}

/** Selector de elementos enfocables por teclado (Tab), excluye `tabindex="-1"`. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** `true` si el elemento es realmente enfocable: visible, no `inert`, no oculto. */
function isKeyboardFocusable(el: HTMLElement): boolean {
  if (el.closest('[inert]')) return false
  if (el.getAttribute('aria-hidden') === 'true') return false
  return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
}

/** Elementos enfocables dentro de `root`, en orden de documento. */
function getFocusable(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    isKeyboardFocusable,
  )
}

/**
 * Encuentra los vecinos enfocables (anterior/siguiente) del grupo del trigger
 * dentro del flujo real de la página, excluyendo el overlay (que vive en un
 * portal al final del `body`). Permite que el foco «salga» del overlay hacia el
 * elemento con el que el usuario inició el flujo, en vez de escapar al final del
 * documento.
 */
function findTriggerNeighbors(
  container: HTMLElement | null,
  overlay: HTMLElement | null,
): { prev: HTMLElement | null; next: HTMLElement | null } {
  if (!container) return { prev: null, next: null }
  const pageFocusable = getFocusable(document.body).filter(
    (el) => !overlay || !overlay.contains(el),
  )
  const insideIndexes = pageFocusable.reduce<number[]>((acc, el, i) => {
    if (container.contains(el)) acc.push(i)
    return acc
  }, [])
  const first = insideIndexes[0]
  const last = insideIndexes[insideIndexes.length - 1]
  if (first === undefined || last === undefined) return { prev: null, next: null }
  return {
    prev: pageFocusable[first - 1] ?? null,
    next: pageFocusable[last + 1] ?? null,
  }
}

/**
 * Lanzador de búsqueda + overlay a pantalla completa (portal, `role="dialog"`).
 * El contenido (sugerencias y productos más buscados) llega por props desde el
 * `module_search` del layout. Cierra con Escape / botón cerrar.
 */
export function SearchInput({
  topSearches = [],
  products = [],
  placeholder = 'Busca por evento, ubicación o fecha',
  onOpenChange,
  variant = 'compact',
  bandStyle = true,
  inverse = false,
}: SearchInputProps) {
  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState(false)
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  /**
   * Input `readOnly` que actúa de lanzador: es el elemento que abre el overlay
   * al recibir foco y al que se devuelve el foco al cerrar (Escape/botón).
   */
  const triggerInputRef = useRef<HTMLInputElement | null>(null)
  /**
   * Botón lanzador de la variante `icon` (no hay input): es el elemento al que
   * se devuelve el foco al cerrar el overlay.
   */
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null)
  /** Contenedor del grupo del trigger, para calcular sus vecinos enfocables. */
  const triggerContainerRef = useRef<HTMLElement | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const overlayInputRef = useRef<HTMLInputElement>(null)
  /**
   * Suprime una única reapertura por foco. Al cerrar devolvemos el foco al
   * input lanzador, cuyo `onFocus` reabriría el overlay; esta bandera consume
   * ese primer `onFocus` para que Escape cierre de verdad y el usuario pueda
   * seguir tabulando sin que el buscador se reabra.
   */
  const suppressOpenRef = useRef(false)
  /** Libera el bloqueo de scroll tomado al abrir (ver `lockDocumentScroll`). */
  const releaseScrollRef = useRef<(() => void) | null>(null)
  const router = useRouter()
  const analytics = useOptionalChromeAnalytics()

  // `view_item_list` de "Productos más buscados" (impresiones agrupadas y
  // deduplicadas — mismo hook que `SearchResults`). `trackSelectItem` NO se
  // pasa aquí: el `select_item` de este módulo necesita su propia redacción
  // de campos (`toSearchProductSelectItem`, más estricta que la genérica
  // `toSelectItem` que usa el hook), así que se dispara directamente más
  // abajo en el `onSelect` de cada card.
  const { trackImpression: trackProductImpression } = useProductListTracking(
    {
      itemListId: SEARCH_PRODUCTS_ITEM_LIST_ID,
      itemListName: SEARCH_PRODUCTS_ITEM_LIST_NAME,
      moduleName: SEARCH_MODULE_NAME,
      moduleType: SEARCH_PRODUCTS_MODULE_TYPE,
      modulePosition: SEARCH_PRODUCTS_MODULE_POSITION,
    },
    {
      trackViewItemList: analytics?.trackViewItemList,
    },
  )

  const openOverlay = () => {
    if (suppressOpenRef.current) {
      suppressOpenRef.current = false
      return
    }
    // En `documentElement`, no en `body`: `html` lleva `overflow-x: clip`, así
    // que un `overflow: hidden` en `body` ya no se propaga al viewport y el
    // header sticky se desanclaba de golpe antes de que el overlay terminara
    // de aparecer.
    // `scrollbar-gutter: stable` conserva el hueco de la barra de scroll al
    // ocultarla, para que el layout no se ensanche (salto horizontal) al
    // completarse la apertura.
    releaseScrollRef.current?.()
    releaseScrollRef.current = lockDocumentScroll()
    document.documentElement.style.scrollbarGutter = 'stable'
    setOpen(true)
    onOpenChange?.(true)
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
  }

  /** Cierra el overlay y, tras la transición, mueve el foco a `focusTarget`. */
  const finishClose = useCallback(
    (focusTarget: HTMLElement | null, { suppressReopen = false } = {}) => {
      setVisible(false)
      setTimeout(() => {
        releaseScrollRef.current?.()
        releaseScrollRef.current = null
        document.documentElement.style.scrollbarGutter = ''
        setOpen(false)
        setQuery('')
        onOpenChange?.(false)
        if (suppressReopen) suppressOpenRef.current = true
        focusTarget?.focus()
      }, 200)
    },
    [onOpenChange],
  )

  const closeOverlay = useCallback(() => {
    // Devolver el foco al input lanzador dispararía su `onFocus` (reapertura):
    // lo suprimimos para que Escape/cerrar deje el foco en el trigger sin reabrir.
    // El lanzador de la variante `icon` es un botón: enfocarlo no reabre nada,
    // así que ahí no hace falta suprimir la reapertura.
    if (triggerInputRef.current) {
      finishClose(triggerInputRef.current, { suppressReopen: true })
      return
    }
    finishClose(triggerButtonRef.current)
  }, [finishClose])

  // `VerticalCard` no expone `onClick`: se cierra el overlay al pulsar
  // cualquier enlace de la card (título extendido o CTA), en captura para no
  // interferir con la navegación ni con `TrackedProductCard`.
  const handleProductClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.target instanceof Element && event.target.closest('a[href]')) closeOverlay()
    },
    [closeOverlay],
  )

  /**
   * Sale del overlay hacia un vecino del trigger. Mueve el foco de inmediato
   * (respuesta ágil al tabular) y luego desmonta el overlay con la transición.
   */
  const exitOverlay = useCallback(
    (target: HTMLElement | null) => {
      // Movemos el foco ya; el cierre solo desmonta (sin volver a enfocar, para
      // no «robar» el foco si el usuario sigue tabulando durante la transición).
      target?.focus()
      finishClose(null)
    },
    [finishClose],
  )

  /**
   * Redirige los límites de tabulación del overlay: Shift+Tab en el primer
   * elemento sale al anterior al trigger; Tab en el último sale al siguiente.
   */
  const handleOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const overlayEl = overlayRef.current
    if (!overlayEl) return
    const focusable = getFocusable(overlayEl)
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    if (e.shiftKey && active === first) {
      e.preventDefault()
      exitOverlay(findTriggerNeighbors(triggerContainerRef.current, overlayEl).prev)
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      exitOverlay(findTriggerNeighbors(triggerContainerRef.current, overlayEl).next)
    }
  }

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeOverlay()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, closeOverlay])

  // Al abrir, enfoca el input de búsqueda VISIBLE. El overlay renderiza dos
  // `SearchBarGlobal` (uno para desktop, otro para móvil/tablet) y CSS oculta
  // con `display:none` el que no corresponde al breakpoint. `overlayInputRef`
  // apunta solo al de desktop, así que en móvil `focus()` era un no-op (no se
  // puede enfocar un elemento `display:none`): el foco se quedaba en el trigger,
  // fuera del portal, el atrapa-foco (`handleOverlayKeyDown`, ligado al overlay)
  // nunca se activaba y Tab escapaba a la página que hay tras el modal. Buscar
  // el primer input realmente enfocable cubre ambos breakpoints.
  useEffect(() => {
    if (!(open && visible)) return
    const overlayEl = overlayRef.current
    if (!overlayEl) return
    const input = getFocusable(overlayEl).find(
      (el): el is HTMLInputElement => el instanceof HTMLInputElement,
    )
    ;(input ?? overlayInputRef.current)?.focus()
  }, [open, visible])

  useEffect(
    () => () => {
      releaseScrollRef.current?.()
      releaseScrollRef.current = null
      document.documentElement.style.scrollbarGutter = ''
    },
    [],
  )

  const goToSearch = (term: string) => {
    router.push(routes.search(term.trim()))
    closeOverlay()
  }

  // `search` (`Header.csv` → "Submit del buscador del header"): SOLO el term
  // libre (Enter/lupa) — a diferencia de clicar un chip de "Top Búsquedas" o
  // una sugerencia de autocompletado, que disparan su propio `click`
  // (`content_link`/`search_autocomplete` respectivamente, más abajo), NO
  // este evento. `results_count`: NOT_SET — el propio diccionario exige
  // disparar ANTES de navegar a la SERP, momento en el que el conteo real
  // todavía no existe (solo lo resuelve `/search` tras la navegación);
  // ambigüedad documentada, no resuelta por Analítica.
  function trackSearchSubmit(term: string) {
    analytics?.trackSearch({
      search_term: slugify(term),
      search_origin: SEARCH_ORIGIN,
      results_count: NOT_SET,
    })
  }

  // Confirmación libre (Enter/botón): navega siempre que haya texto, aunque
  // esté por debajo del umbral de `MIN_QUERY_LENGTH` — `/search` es quien
  // muestra el aviso de mínimo de caracteres (FR-002), no el header.
  const handleSearch = (q: string) => {
    const trimmed = q.trim()
    if (!trimmed) return
    trackSearchSubmit(trimmed)
    goToSearch(trimmed)
  }

  // Autocompletado: al escribir, llama (con debounce) al servicio
  // `/search/autocomplete?q=…` y muestra las sugerencias.
  useEffect(() => {
    const term = query.trim()
    if (!term) return
    const id = setTimeout(() => {
      searchAutocompleteAction(term).then(setSuggestions)
    }, 250)
    return () => clearTimeout(id)
  }, [query])

  const q = query.trim()

  const overlay = open
    ? createPortal(
        <div
          ref={overlayRef}
          className={styles.overlay}
          data-visible={visible ? '' : undefined}
          role="dialog"
          aria-modal="true"
          aria-label="Buscador"
          onKeyDown={handleOverlayKeyDown}
        >
          <div className={styles.overlayHeader}>
            <div className={styles.overlayHeaderInner}>
              <div className={styles.overlayStart}>
                <button
                  onClick={closeOverlay}
                  aria-label="Cerrar búsqueda"
                  className={cx(styles.overlayIconBtn, styles.closeBtn)}
                >
                  <IconClose size={15.56} />
                </button>

                <div className={styles.overlayBrand}>
                  <Wordmark height={24} className={styles.overlayWordmark} />
                </div>
              </div>

              <div className={styles.overlaySearchForm}>
                <SearchBarGlobal
                  ref={overlayInputRef}
                  value={query}
                  onChange={setQuery}
                  onSearch={handleSearch}
                  placeholder={placeholder}
                  autoFocus={false}
                />
              </div>

              <div className={styles.overlayEnd} aria-hidden="true" />
            </div>

            <div className={styles.overlaySearchRowMobile}>
              <SearchBarGlobal
                value={query}
                onChange={setQuery}
                onSearch={handleSearch}
                placeholder={placeholder}
                autoFocus={false}
              />
            </div>
          </div>

          <div className={styles.overlayContent}>
            {q ? (
              <section className={styles.section}>
                <div className={styles.suggestionsBlock}>
                  <div>
                    <h3 className={styles.sectionTitle}>
                      {suggestions.length > 0 ? (
                        'Sugerencias'
                      ) : (
                        <>Resultados para &ldquo;{query}&rdquo;</>
                      )}
                    </h3>
                    {suggestions.length > 0 ? (
                      <ul className={styles.suggestionList}>
                        {suggestions.map((s, index) => (
                          <li key={s.label}>
                            <button
                              type="button"
                              className={styles.suggestion}
                              onClick={() => {
                                // `click`/`search_autocomplete` (`Header.csv` →
                                // "Click en sugerencia de autocomplete") — NO
                                // dispara `search` (ver `trackSearchSubmit`):
                                // esta fila del CSV documenta su propio evento
                                // `click`, distinto del submit libre.
                                analytics?.trackClick({
                                  click_type: 'search_autocomplete',
                                  click_subtype: NO_APLICA,
                                  link_text: NO_APLICA,
                                  link_url: NO_APLICA,
                                  link_domain: NO_APLICA,
                                  module_name: SEARCH_MODULE_NAME,
                                  module_type: NO_APLICA,
                                  nav_level: NO_APLICA,
                                  nav_path: NO_APLICA,
                                  action_detail: slugify(s.label),
                                  action_value: String(index + 1),
                                  action_context: NO_APLICA,
                                })
                                goToSearch(s.label)
                              }}
                            >
                              <span className={styles.suggestionText}>
                                {highlightSearchTerm(s.label, q)}
                              </span>
                              <span className={styles.suggestionArrow} aria-hidden="true">
                                <IconArrowRight size={16} />
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.noResults}>Sin resultados para &ldquo;{query}&rdquo;</p>
                    )}
                  </div>
                </div>
              </section>
            ) : (
              <>
                {topSearches.length > 0 && (
                  <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Top Búsquedas</h3>
                    <div className={styles.chips}>
                      {/* Enlaces (no botones que hacen `router.push`): son
                          navegación, y así `NavigationProgress` detecta el
                          click y muestra el spinner mientras carga la SERP.
                          Sin prefetch: cada apertura del overlay precargaría
                          la SERP de todos los términos (una búsqueda real por
                          chip), igual que se evitó en los chips de SearchBand. */}
                      {topSearches.map(({ term }) => (
                        <Link
                          key={term}
                          href={routes.search(term.trim())}
                          prefetch={false}
                          className={styles.chip}
                          onClick={() => {
                            // `click`/`content_link` (`Resultados de
                            // búsqueda.csv` → «Click en sugerencia de Top
                            // búsquedas») — NO dispara `search` (ver
                            // `trackSearchSubmit`): el chip es un término fijo
                            // elegido, no una búsqueda libre tecleada por el
                            // usuario, y esta ficha documenta su propio
                            // evento `click`, igual que la sugerencia de
                            // autocompletado más arriba.
                            analytics?.trackClick({
                              click_type: 'content_link',
                              click_subtype: NO_APLICA,
                              link_text: slugify(term),
                              link_url: routes.search(term),
                              link_domain: NO_APLICA,
                              module_name: TOP_SEARCHES_MODULE_NAME,
                              module_type: TOP_SEARCHES_MODULE_TYPE,
                              nav_level: NO_APLICA,
                              nav_path: NO_APLICA,
                              action_detail: NO_APLICA,
                              action_value: NO_APLICA,
                              action_context: NO_APLICA,
                            })
                            closeOverlay()
                          }}
                        >
                          {term}
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {products.length > 0 && (
                  <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Productos más buscados</h3>
                    <ul className={styles.productGrid}>
                      {products.map((p, index) => (
                        <li
                          key={index}
                          className={styles.productItem}
                          onClickCapture={handleProductClick}
                        >
                          <TrackedProductCard
                            index={index}
                            onImpression={() =>
                              trackProductImpression(
                                p.id,
                                mapSearchProductToAnalyticsItem(p, index),
                              )
                            }
                            onSelect={() => {
                              // `select_item` (`Resultados de búsqueda.csv` →
                              // «Click en card de Productos más buscados»):
                              // redacción estricta propia de este módulo, ver
                              // `toSearchProductSelectItem`.
                              analytics?.trackSelectItem({
                                item_list_id: SEARCH_PRODUCTS_ITEM_LIST_ID,
                                item_list_name: SEARCH_PRODUCTS_ITEM_LIST_NAME,
                                module_name: SEARCH_MODULE_NAME,
                                module_type: SEARCH_PRODUCTS_MODULE_TYPE,
                                // `no_aplica` en `select_item` (a diferencia de
                                // `view_item_list`), mismo criterio que el
                                // resto del catálogo.
                                module_position: NO_APLICA,
                                items: [
                                  toSearchProductSelectItem(
                                    mapSearchProductToAnalyticsItem(p, index),
                                  ),
                                ],
                              })
                            }}
                          >
                            <VerticalCard {...mapSearchProductToCard(p)} />
                          </TrackedProductCard>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </div>
        </div>,
        document.body,
      )
    : null

  // Lanzador de solo icono (variante `icon`): un botón, no un input de solo
  // lectura, porque en la barra de navegación no hay campo visible que enfocar
  // — el `aria-haspopup="dialog"` anuncia que abre el overlay de búsqueda.
  const iconTrigger = (
    <div
      className={styles.iconTrigger}
      ref={(el) => {
        triggerContainerRef.current = el
      }}
    >
      <button
        ref={triggerButtonRef}
        type="button"
        className={styles.iconTriggerBtn}
        aria-label="Abrir buscador"
        aria-haspopup="dialog"
        onClick={openOverlay}
      >
        <IconSearch size={24} />
      </button>
    </div>
  )

  const trigger =
    variant === 'icon' ? (
      iconTrigger
    ) : variant === 'full' ? (
      <div
        ref={(el) => {
          triggerContainerRef.current = el
        }}
        tabIndex={-1}
        className={styles.fullTrigger}
        onClick={openOverlay}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openOverlay()
        }}
      >
        <SearchBarGlobal
          ref={triggerInputRef}
          readOnly
          placeholder={placeholder}
          onFocus={openOverlay}
          variant={inverse ? 'inverse' : undefined}
          className={bandStyle ? styles.searchBandBar : undefined}
        />
      </div>
    ) : (
      <div
        className={styles.wrapper}
        ref={(el) => {
          triggerContainerRef.current = el
        }}
      >
        <div className={styles.inputBox}>
          <span className={styles.lupaIcon} aria-hidden="true">
            <IconSearch size={16} />
          </span>
          <input
            ref={triggerInputRef}
            readOnly
            placeholder={placeholder}
            className={styles.input}
            aria-label="Abrir buscador"
            aria-haspopup="dialog"
            onFocus={openOverlay}
          />
        </div>
        <button
          type="button"
          aria-label="Buscar"
          className={styles.searchBtn}
          onClick={openOverlay}
        >
          <IconSearch size={18} />
        </button>
      </div>
    )

  return (
    <>
      {trigger}
      {overlay}
    </>
  )
}
